import React, { useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RadiologyIcon from '@mui/icons-material/MonitorHeart'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import Age from '../../../../components/Age'
import {
  InfoList,
  SectionHeader,
} from '../../../fleetEquipment/vehicles/VehicleComponents/SectionKit'

// Right-hand drawer for one imaging request — the approver's review (approve
// / reject) while it is Requested, and a read-only summary of the decision
// and study afterwards. Used from the approver worklist and the ePCR imaging
// panel. `request` drives open/closed; onDecided(updated) after a decision.

export const IMAGING_STATUS_COLORS = {
  Requested: 'warning',
  Approved: 'info',
  Completed: 'success',
  Rejected: 'error',
  Cancelled: 'default',
}

export const IMAGING_STATUS_LABELS = {
  Requested: 'Awaiting approval',
  Approved: 'Approved — awaiting study',
  Completed: 'Completed',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
}

export const fullName = (user) => [user?.firstName, user?.lastName].filter(Boolean).join(' ')

export const bodyLocation = (request) =>
  [
    (request?.bodyRegions || []).join(', '),
    request?.laterality && request.laterality !== 'Not Applicable' ? request.laterality : '',
  ]
    .filter(Boolean)
    .join(' — ')

export const patientLabel = (epcr) => {
  const d = epcr?.demographics
  if (!d?.firstName && !d?.lastName) return 'Unnamed patient'
  return [d.lastName?.toUpperCase(), d.firstName].filter(Boolean).join(', ')
}

export const ModalityChip = ({ modality, size = 'small' }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Chip
      size={size}
      label={modality?.label ?? 'Imaging'}
      sx={{ borderRadius: 1, fontWeight: 700 }}
    />
    {modality?.ionising && (
      <Chip
        size={size}
        label="Ionising"
        color="warning"
        variant="outlined"
        sx={{ borderRadius: 1, fontWeight: 700 }}
      />
    )}
  </Stack>
)

const Section = ({ title, caption, children }) => (
  <Stack spacing={1.25}>
    <SectionHeader title={title} caption={caption} />
    {children}
  </Stack>
)

const Card = ({ children, sx }) => (
  <Paper
    elevation={0}
    sx={{ p: 1.5, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}`, ...sx }}
  >
    {children}
  </Paper>
)

const Person = ({ user, caption }) => (
  <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
    <Avatar src={user?.photograph} sx={{ width: 30, height: 30, fontSize: '0.8rem' }}>
      {user?.firstName?.[0]}
    </Avatar>
    <Box minWidth={0}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {fullName(user) || '—'}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {caption}
        </Typography>
      )}
    </Box>
  </Stack>
)

// The radiation-safety gate shown above the decision: ionising studies can't
// be approved without a recorded pregnancy status (mirrors the server rule).
const SafetyCheck = ({ request }) => {
  if (!request.modality?.ionising) {
    return (
      <Alert severity="info" variant="outlined" icon={<CheckCircleOutlineIcon />}>
        Non-ionising study — no radiation checks needed.
      </Alert>
    )
  }
  if (!request.pregnancyStatus) {
    return (
      <Alert severity="error" icon={<ErrorOutlineIcon />}>
        <strong>Pregnancy status not recorded.</strong> This ionising study can't be approved
        until the requesting clinician records it on the ePCR.
      </Alert>
    )
  }
  const concern = ['Pregnant', 'Unknown'].includes(request.pregnancyStatus)
  return (
    <Alert severity={concern ? 'warning' : 'success'} icon={concern ? undefined : <CheckCircleOutlineIcon />}>
      Pregnancy status: <strong>{request.pregnancyStatus}</strong>
      {concern && ' — consider whether the benefit justifies the exposure.'}
    </Alert>
  )
}

const ImagingReviewDrawer = ({ request, onClose, onDecided, canDecide = true, showRecordLink = true }) => {
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()
  const [notes, setNotes] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const notesRef = useRef(null)

  // Bring the reason box into view when the approver chooses Reject
  useEffect(() => {
    if (!rejecting || !notesRef.current) return
    notesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    notesRef.current.querySelector('textarea')?.focus({ preventScroll: true })
  }, [rejecting])

  useEffect(() => {
    setNotes('')
    setRejecting(false)
    setError(null)
  }, [request?._id])

  // Keep the last request rendered while the drawer slides closed
  const [shown, setShown] = useState(request)
  useEffect(() => {
    if (request) setShown(request)
  }, [request])
  const r = request ?? shown

  const pending = r?.status === 'Requested'
  const pregnancyMissing = r?.modality?.ionising && !r?.pregnancyStatus
  const epcr = r?.epcr && typeof r.epcr === 'object' ? r.epcr : null
  const epcrID = epcr?._id ?? r?.epcr

  const handleClose = () => {
    if (saving) return
    if (notes.trim() && pending && !window.confirm('Discard your decision notes?')) return
    onClose()
  }

  const decide = (decision) => {
    if (decision === 'Rejected' && !notes.trim()) {
      setError('Give a reason so the requesting clinician knows why.')
      return
    }
    setSaving(true)
    setError(null)
    API.post(`/imaging-requests/${r._id}/decision`, { decision, decisionNotes: notes.trim() })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: `Request ${decision.toLowerCase()}` })
          onDecided?.(res.data)
        } else {
          setError(res.data?.errorMessage ?? res.data?.message ?? 'Unable to save the decision.')
        }
      })
      .catch(() => setError('Unable to save the decision.'))
      .finally(() => setSaving(false))
  }

  const dose = r?.dose
  const doseText =
    dose?.value != null
      ? [
          `${dose.value} ${dose.unit ?? ''}`.trim(),
          dose.exposures && `${dose.exposures} exposure${dose.exposures === 1 ? '' : 's'}`,
          dose.device,
        ]
          .filter(Boolean)
          .join(' · ')
      : null

  return (
    <Drawer
      open={!!request}
      anchor="right"
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100vw', sm: 480, lg: 540 }, maxWidth: '100vw', bgcolor: 'background.default' },
      }}
    >
      {r && (
        <Stack height="100%">
          {/* Header */}
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: r.modality?.ionising ? 'rgba(237, 108, 2, 0.12)' : 'rgba(2, 136, 209, 0.12)',
                  color: r.modality?.ionising ? 'warning.main' : 'info.main',
                  flexShrink: 0,
                }}
              >
                <RadiologyIcon />
              </Box>
              <Box minWidth={0} flex={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                    {r.modality?.label ?? 'Imaging'} request
                  </Typography>
                  <Chip
                    size="small"
                    label={IMAGING_STATUS_LABELS[r.status] ?? r.status}
                    color={IMAGING_STATUS_COLORS[r.status] ?? 'default'}
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Requested {moment(r.createdAt).format('Do MMM YYYY [at] HH:mm')} ({moment(r.createdAt).fromNow()})
                </Typography>
              </Box>
              <IconButton onClick={handleClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          <Stack spacing={3} sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            {epcr && (
              <Card>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                  <Box minWidth={0}>
                    <Typography variant="body1" fontWeight={700} noWrap>
                      {patientLabel(epcr)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {[
                        epcr.demographics?.dob &&
                          `${Age(epcr.demographics.dob, epcr.createdAt)} (DOB ${moment(epcr.demographics.dob).format('DD/MM/YYYY')})`,
                        epcr.epcrNumber && `ePCR #${epcr.epcrNumber}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                  {showRecordLink && epcrID && (
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                      onClick={() => navigate(`/patient-report-forms/${epcrID}`)}
                      sx={{ borderRadius: 2, textTransform: 'none', flexShrink: 0 }}
                    >
                      Open record
                    </Button>
                  )}
                </Stack>
              </Card>
            )}

            <Section title="Request">
              <Card>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      CLINICAL INDICATION
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mt: 0.25 }}>
                      {r.clinicalIndication || '—'}
                    </Typography>
                  </Box>
                  <InfoList
                    rows={[
                      { label: 'Modality', value: <ModalityChip modality={r.modality} /> },
                      { label: 'Body location', value: bodyLocation(r) || null },
                      ...(r.modality?.ionising
                        ? [{ label: 'Pregnancy status', value: r.pregnancyStatus || 'Not recorded' }]
                        : []),
                    ]}
                  />
                  <Person
                    user={r.requestedBy}
                    caption={`Requested ${moment(r.createdAt).format('DD/MM/YYYY HH:mm')}`}
                  />
                </Stack>
              </Card>
            </Section>

            {pending ? (
              <Section title="Your decision" caption={canDecide ? 'Approve to let the crew perform the study' : undefined}>
                <SafetyCheck request={r} />
                {canDecide ? (
                  <TextField
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value)
                      if (error) setError(null)
                    }}
                    label={rejecting ? 'Reason for rejecting' : 'Decision notes (optional)'}
                    helperText={
                      rejecting
                        ? 'Required — sent to the requesting clinician'
                        : 'Shared with the requesting clinician. Required if you reject.'
                    }
                    ref={notesRef}
                    required={rejecting}
                    error={!!error && rejecting}
                    multiline
                    minRows={3}
                    fullWidth
                    sx={{ bgcolor: 'background.paper' }}
                  />
                ) : (
                  <Alert severity="info">Only configured imaging approvers can decide this request.</Alert>
                )}
                {error && <Alert severity="error">{error}</Alert>}
              </Section>
            ) : (
              <>
                {['Approved', 'Rejected'].includes(r.status) || r.decidedBy ? (
                  <Section title="Decision">
                    <Card
                      sx={{
                        borderLeft: (t) =>
                          `4px solid ${r.status === 'Rejected' ? t.palette.error.main : t.palette.success.main}`,
                      }}
                    >
                      <Stack spacing={1}>
                        <Person
                          user={r.decidedBy}
                          caption={`${r.status === 'Rejected' ? 'Rejected' : 'Approved'} ${
                            r.decidedAt ? moment(r.decidedAt).format('DD/MM/YYYY HH:mm') : ''
                          }`}
                        />
                        {r.decisionNotes && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {r.decisionNotes}
                          </Typography>
                        )}
                      </Stack>
                    </Card>
                  </Section>
                ) : null}

                {r.status === 'Cancelled' && (
                  <Alert severity="info">
                    Cancelled by {fullName(r.cancelledBy) || 'a clinician'}
                    {r.cancelledAt && ` on ${moment(r.cancelledAt).format('DD/MM/YYYY HH:mm')}`}.
                  </Alert>
                )}

                {['Approved', 'Completed'].includes(r.status) && (
                  <Section
                    title="Study"
                    caption={r.status === 'Approved' ? 'Waiting for the crew to record the result' : undefined}
                  >
                    <Card>
                      {r.clinicalImpression?.text ? (
                        <Stack spacing={1.5}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                              CLINICAL IMPRESSION
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mt: 0.25 }}>
                              {r.clinicalImpression.text}
                            </Typography>
                          </Box>
                          <InfoList
                            rows={[
                              { label: 'Performed by', value: fullName(r.performedBy) || null },
                              {
                                label: 'Recorded',
                                value: r.clinicalImpression.recordedAt
                                  ? `${moment(r.clinicalImpression.recordedAt).format('DD/MM/YYYY HH:mm')} by ${fullName(
                                      r.clinicalImpression.recordedBy,
                                    )}`
                                  : null,
                              },
                              ...(r.modality?.ionising ? [{ label: 'Radiation dose', value: doseText }] : []),
                              { label: 'Images', value: `${(r.images || []).length} attached` },
                            ]}
                          />
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No clinical impression recorded yet
                          {(r.images || []).length > 0 && ` · ${r.images.length} image${r.images.length === 1 ? '' : 's'} attached`}.
                        </Typography>
                      )}
                    </Card>
                  </Section>
                )}
              </>
            )}
          </Stack>

          {/* Sticky decision footer */}
          {pending && canDecide && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ p: 2, bgcolor: 'background.paper', borderTop: (t) => `1px solid ${t.palette.divider}` }}
            >
              {rejecting ? (
                <>
                  <Button fullWidth onClick={() => setRejecting(false)} disabled={saving} color="inherit">
                    Back
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    startIcon={<DoNotDisturbAltIcon />}
                    disabled={saving || !notes.trim()}
                    onClick={() => decide('Rejected')}
                  >
                    Confirm reject
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DoNotDisturbAltIcon />}
                    disabled={saving}
                    onClick={() => (notes.trim() ? decide('Rejected') : setRejecting(true))}
                  >
                    Reject
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={saving || pregnancyMissing}
                    onClick={() => decide('Approved')}
                  >
                    Approve
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </Drawer>
  )
}

export default ImagingReviewDrawer
